import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const d = (ms: number) => `${ms}ms`;

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-12 sm:py-20 bg-[#FAFAF8] font-body overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section label */}
        <div
          className={`flex items-center gap-3 mb-10 sm:mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="w-8 h-[1px] bg-[#F5C518]"></div>
          <span className="text-[#F5C518] text-xs font-semibold tracking-[0.3em] uppercase">
            About Me
          </span>
        </div>

        {/* Big intro — staggered left-right layout */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-start mb-10 sm:mb-16">
          {/* Left: Name + title */}
          <div>
            <h2
              className={`text-5xl sm:text-7xl md:text-8xl font-display tracking-wide text-[#1a1a1a] leading-[0.95] transition-all duration-800 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
              style={{ transitionDelay: d(100) }}
            >
              HI, I&apos;M
            </h2>
            <h2
              className={`text-5xl sm:text-7xl md:text-8xl font-display tracking-wide leading-[0.95] transition-all duration-800 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
              style={{ transitionDelay: d(200) }}
            >
              <span className="text-[#F5C518]">YASMINE</span>
              <span className="text-[#1a1a1a]">.</span>
            </h2>
            <div
              className={`mt-6 flex items-center gap-4 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
              style={{ transitionDelay: d(400) }}
            >
              <div className="w-10 h-[2px] bg-[#F5C518]"></div>
              <p className="text-[#999] text-xs sm:text-sm tracking-[0.2em] uppercase font-medium">
                Founder &middot; Strategist &middot; Developer
              </p>
            </div>
          </div>

          {/* Right: Opening statement */}
          <div
            className={`lg:pt-4 transition-all duration-800 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: d(350) }}
          >
            <p className="text-2xl sm:text-3xl md:text-[32px] font-light text-[#1a1a1a] leading-snug">
              I don&apos;t just build websites.
              <br />
              <strong className="font-semibold">
                I build digital infrastructure
              </strong>{' '}
              for small businesses and solopreneurs.
            </p>
          </div>
        </div>

        {/* Divider with accent */}
        <div
          className={`flex items-center gap-6 mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: d(500) }}
        >
          <div className="flex-1 h-[1px] bg-[#eee]"></div>
          <div className="w-2 h-2 bg-[#F5C518] rotate-45"></div>
          <div className="flex-1 h-[1px] bg-[#eee]"></div>
        </div>

        {/* Story blocks — alternating emphasis */}
        <div className="max-w-5xl mx-auto space-y-10 sm:space-y-14">
          {/* Block 1 */}
          <div
            className={`grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start transition-all duration-800 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: d(550) }}
          >
            <div className="hidden md:flex flex-col items-center pt-1">
              <div className="w-[1px] h-full bg-[#eee]"></div>
            </div>
            <div>
              <p className="text-[#555] text-base sm:text-lg leading-[1.85]">
                As a business owner myself, I understand what it means to
                operate with{' '}
                <strong className="text-[#1a1a1a] font-semibold">
                  limited time, limited resources, and big vision
                </strong>
                . I&apos;ve built brands from scratch, developed tech platforms,
                built content ecosystems, and created systems that automate and
                organize operations behind the scenes.{' '}
                <strong className="text-[#1a1a1a] font-semibold">
                  That perspective{' '}
                  <span className="text-[#F5C518]">changes</span> how I design.
                </strong>{' '}
                When I build a website, I&apos;m thinking beyond aesthetics.
                I&apos;m thinking about structure, scalability, user behavior,
                and long‑term sustainability.
              </p>
            </div>
          </div>

          {/* Block 3 — Approach pillars */}
          <div
            className={`transition-all duration-800 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: d(850) }}
          >
            <p className="text-[#999] text-xs tracking-[0.3em] uppercase font-semibold mb-8">
              My Approach Is Simple
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[
                { label: 'Clean Design', icon: 'ri-layout-line' },
                { label: 'Clear Messaging', icon: 'ri-chat-quote-line' },
                { label: 'Strong User Flow', icon: 'ri-route-line' },
                { label: 'Professional Presentation', icon: 'ri-award-line' },
              ].map((item, i) => (
                <div
                  key={item.label}
                  className={`group bg-white border border-[#eee] rounded-lg p-5 sm:p-6 hover:border-[#F5C518]/40 hover:shadow-md transition-all duration-500 cursor-default ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: d(950 + i * 100) }}
                >
                  <div className="w-9 h-9 rounded-md bg-[#F5C518]/10 flex items-center justify-center mb-4 group-hover:bg-[#F5C518]/20 transition-colors">
                    <i className={`${item.icon} text-[#F5C518] text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <p className="text-[#1a1a1a] text-sm font-semibold tracking-wide">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
